import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { ItemType } from '@/common/api/types/itemType';
import { itemTypesQueryOptions } from '@/common/queries/useItemTypesQuery';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { associateById } from '@/common/utils/associateById';
import { usePublishedContext } from '@/features/views/components/published/PublishedContext';
import { usePublishedSnapshotQuery } from '@/features/views/components/published/usePublishedSnapshotQuery';

export const useItemTypesQueryWithPublished = (
  options: Partial<UseQueryOptions<ItemType[]>> = {},
) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const itemTypesQuery = useQuery({
    ...itemTypesQueryOptions(),
    ...options,
    enabled: !isPublished && options.enabled !== false,
  });

  const publishedQuery = usePublishedSnapshotQuery({
    baseKey: commonQueryKeys.itemTypes.all,
    getSnapshot: () => publishedContext?.itemTypes,
  });

  return isPublished ? publishedQuery : itemTypesQuery;
};

export const useItemTypesByIdQueryWithPublished = (
  options: Partial<UseQueryOptions<ItemType[]>> = {},
) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const itemTypesQuery = useQuery({
    ...itemTypesQueryOptions(),
    ...options,
    select: associateById,
    enabled: !isPublished && options.enabled !== false,
  });

  const publishedQuery = usePublishedSnapshotQuery<ItemType[], Record<number, ItemType>>({
    baseKey: commonQueryKeys.itemTypes.all,
    getSnapshot: () => publishedContext?.itemTypes,
    select: (data) => (data ? associateById(data) : {}),
  });

  return isPublished ? publishedQuery : itemTypesQuery;
};
